"use client";
import { useState, useMemo, useCallback } from "react";
import Link from "next/link";
import { ArrowLeft, Loader2, Check, Camera } from "lucide-react";
import { Button } from "@/app/components/shadcn/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/components/shadcn/select";
import FallbackImage from "@/app/components/elements/FallbackImage";
import FavoriteButton from "@/app/components/elements/buttons/FavoriteButton";
import StarRating from "@/app/components/elements/StarRating";
import ProductCardPrice from "@/app/components/elements/cards/productCard/ProductCardPrice";
import { ReviewProductData } from "@/app/lib/features/productReview/productReview.types";
import useCart from "@/app/lib/features/cart/cart.hook";
import useModal from "@/app/lib/features/modal/modal.hook";
import { toast } from "sonner";
import { trackProductAddToCart, trackProductClick } from "@/app/lib/tracking/interactions";
import Image from "next/image";
import RotatingText from "@/app/components/common/sections/RotationText";

interface ReviewProductCardProps {
  data: ReviewProductData;
  productSlug: string;
}

const ReviewProductCard = ({ data, productSlug }: ReviewProductCardProps) => {
  const { addToCart } = useCart();
  const { createModal } = useModal();

  // Build social proof items manually for review page
  const socialProofItems = useMemo(() => {
    if (!data.social_proof?.items) return [];
    return data.social_proof.items.map((item) => ({
      text: item.text,
      icon: <Image src={item.icon_full} alt="" width={12} height={12} className="shrink-0" />,
      style: {
        color: item.color_hex,
      },
    }));
  }, [data.social_proof]);

  // Build campaign items manually for review page
  const campaignItems = useMemo(() => {
    if (!data.campaigns || data.campaigns.length === 0) return [];
    return data.campaigns.map((campaign) => ({
      text: campaign.name,
      icon: <Image src={campaign.icon_full} alt="" width={12} height={12} className="shrink-0" />,
      style: {
        color: campaign.color_hex,
      },
    }));
  }, [data.campaigns]);
  
  const badgeItems = useMemo(() => {
    if (!data.badges || data.badges.length === 0) return [];
    return data.badges.map((badge) => ({
      text: badge.label,
      icon: badge.icon_full ? (
        <Image
          src={badge.icon_full}
          alt={badge.label}
          width={12}
          height={12}
          className="shrink-0"
        />
      ) : undefined,
      style: {
        backgroundColor: badge.color_hex,
        color: "white",
      },
    }));
  }, [data.badges]);

  // Get current color variant (the one matching current variant_id)
  const currentColorVariant = useMemo(() => {
    if (!data.variants_by_color || data.variants_by_color.length === 0) return null;
    
    // Find the variant that has a size matching the page's variant_id
    const matchingVariant = data.variants_by_color.find(colorVariant => 
      colorVariant.sizes.some(size => size.variant_id === data.variant_id)
    );
    
    return matchingVariant || data.variants_by_color[0];
  }, [data.variants_by_color, data.variant_id]);

  // Get available sizes from current color variant
  const availableSizes = useMemo(() => {
    if (!currentColorVariant?.sizes) return [];
    return currentColorVariant.sizes;
  }, [currentColorVariant]);

  // Find the initial selected size index based on page's variant_id (useMemo instead of useEffect)
  const initialSizeIndex = useMemo(() => {
    if (availableSizes.length === 0) return 0;
    
    const index = availableSizes.findIndex(
      size => size.variant_id === data.variant_id
    );
    return index !== -1 ? index : 0;
  }, [availableSizes, data.variant_id]);

  const [selectedSizeIndex, setSelectedSizeIndex] = useState(initialSizeIndex);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [cartAdded, setCartAdded] = useState(false);

  // Get selected size object
  const selectedSize = useMemo(() => {
    return availableSizes[selectedSizeIndex] || null;
  }, [availableSizes, selectedSizeIndex]);

  const handleAddToCart = useCallback(async () => {
    if (!availableSizes || availableSizes.length === 0) {
      toast.error("Bu ürün için beden bulunamadı");
      return;
    }

    if (!selectedSize) {
      toast.error("Lütfen bir beden seçin");
      return;
    }

    // Check stock
    if (selectedSize.stock === 0) {
      toast.error("Seçili beden stokta yok");
      return;
    }

    setIsAddingToCart(true);

    const { success, message, price: cartPrice } = await addToCart(
      { product_id: data.id, variant_id: selectedSize.variant_id, quantity: 1 },
      false,
      { name: data.name, price: selectedSize?.price ?? 0 },
    );

    if (success) {
      trackProductAddToCart(data.id);
      createModal("added-to-cart", {
        image: currentColorVariant?.primary_image ?? "",
        name: data.name,
        colorName: currentColorVariant?.color_name,
        sizeName: selectedSize?.size,
        price: `${(cartPrice ?? selectedSize?.price ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`,
      });
      setCartAdded(true);

      setTimeout(() => {
        setCartAdded(false);
        setIsAddingToCart(false);
      }, 2000);
    } else {
      toast.error(message || "Sepete eklenirken bir hata oluştu");
      setIsAddingToCart(false);
    }
  }, [data.id, selectedSize, availableSizes, addToCart]);

  const isOutOfStock = selectedSize?.stock === 0;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 relative">
      {/* Back Button */}
      <Link
        href={`/${productSlug}`}
        onClick={() => trackProductClick(data.id)}
        className="absolute top-4 right-4 z-10 inline-flex items-center justify-center rounded-md h-10 w-10 hover:bg-gray-100 transition-colors"
      >
        <ArrowLeft size={20} />
      </Link>

      <div className="flex gap-4">
        {/* Left: Product Image */}
        <div className="relative w-32 h-40 shrink-0 rounded-lg overflow-hidden bg-gray-100">
          <FallbackImage
            src={data.image || data.first_image}
            alt={data.internet_product_description}
            fill
            className="object-cover"
            fallbackClassName="object-contain p-4 opacity-60"
            sizes="128px"
          />
        </div>

        {/* Right: Product Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Product Title - Brand + Name */}
          <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 leading-tight mb-2 pr-12">
            {data.seller?.name && <span className="font-medium">{data.seller.name}</span>} {data.internet_product_description}
          </h3>

          {/* Badges - Rotating */}
          {badgeItems.length > 0 && (
            <div className="mb-1">
              <RotatingText
                items={badgeItems}
                className="text-[10px] text-white font-medium py-0.5 px-2 rounded inline-flex"
                rotationInterval={3000}
                loop={true}
                auto={true}
              />
            </div>
          )}

          {/* Social Proof - Rotating */}
          {socialProofItems.length > 0 && (
            <div className="mb-1">
              <RotatingText
                items={socialProofItems}
                className="text-xs justify-start py-0"
                rotationInterval={3000}
              />
            </div>
          )}

          {/* Rating & Reviews */}
          {data.rating && data.rating.count > 0 && (
            <div className="flex items-center gap-2 mb-1">
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">
                  ({data.rating.average.toFixed(1)})
                </span>
                <StarRating rating={data.rating.average} size="xs" />
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">
                  ({data.rating.count})
                </span>
                <Camera className="w-3 h-3 text-muted-foreground" />
              </div>
            </div>
          )}

          {/* Campaigns - Rotating */}
          {campaignItems.length > 0 && (
            <div className="mb-2">
              <RotatingText
                items={campaignItems}
                className="text-xs justify-start py-0"
                rotationInterval={2500}
              />
            </div>
          )}

          {/* Price - Using ProductCardPrice */}
          <div className="mb-3">
            <ProductCardPrice
              price={data.price}
              listPrice={data.list_price}
              hasDiscount={data.has_discount}
              hasCartSpecialPrice={data.has_cart_special_price}
              cartSpecialPriceText={data.cart_special_price_text}
              currencySymbol={data.currency_symbol}
            />
          </div>

          {/* Actions - Select + Buttons */}
          <div className="flex items-center gap-2 mt-auto">
            {/* Size Selection */}
            {availableSizes.length > 0 && (
              <Select 
                value={selectedSizeIndex.toString()} 
                onValueChange={(value) => setSelectedSizeIndex(parseInt(value))}
              >
                <SelectTrigger className="h-10 md:h-12 flex-1">
                  <SelectValue placeholder={data.size_selector?.label || "Beden"} />
                </SelectTrigger>
                <SelectContent>
                  {availableSizes.map((size, index) => (
                    <SelectItem 
                      key={index} 
                      value={index.toString()}
                      disabled={size.stock === 0}
                    >
                      {size.size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* Add to Cart */}
            <Button 
              className="h-10 md:h-12 flex-1"
              onClick={handleAddToCart}
              disabled={isAddingToCart || isOutOfStock || availableSizes.length === 0}
            >
              {isAddingToCart ? (
                cartAdded ? (
                  <>
                    <Check className="mr-1 h-4 w-4" />
                    Eklendi
                  </>
                ) : (
                  <>
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    Ekleniyor
                  </>
                )
              ) : isOutOfStock ? (
                "Stokta Yok"
              ) : (
                data.action_button?.text || "Sepete Ekle"
              )}
            </Button>
            
            {/* Favorite */}
            <FavoriteButton
              productId={data.id}
              /* Favori renk varyantı bazında — seçili beden değil, ürünün
                 gösterilen varyantı. */
              variantId={data.variant_id}
              initialIsFavorited={data.is_favorited}
              className="h-10 w-10 md:h-12 md:w-12 rounded-full shrink-0"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewProductCard;